import { serializeLexical } from '../workflow/getArgumentValue/serializeLexical.js';

/**
 * Deeply compares two objects and generates diffs in the setDeep format.
 *
 * @param {Object} prevObj - The previous (original) object.
 * @param {Object} newObj - The new (modified) object.
 * @param {string} [path=''] - The current path (used for recursion).
 * @returns {Array} An array of diffs in the format { key, value }.
 */
export const generateDeepDiffs = (prevObj = {}, newObj = {}, path = '') => {
  const diffs = [];

  const buildPath = (currentPath, key) => (currentPath ? `${currentPath}.${key}` : key);

  const handleArrayDiffs = (prevArr, newArr, arrPath) => {
    // Prefer identity-based diffs when array items are identifiable by an id-like property
    const getIdPropFromArray = (arr) => {
      for (const item of arr) {
        if (item && typeof item === 'object') {
          const candidate = Object.keys(item).find((key) => key === 'id' || key.endsWith('Id') || key.endsWith('id'));
          if (candidate) {
            return candidate;
          }
        }
      }
      return null;
    };

    const idProp = getIdPropFromArray(newArr) || getIdPropFromArray(prevArr);

    if (idProp) {
      const prevMap = new Map();
      const newMap = new Map();

      for (const item of prevArr) {
        if (item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, idProp)) {
          prevMap.set(item[idProp], item);
        }
      }
      for (const item of newArr) {
        if (item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, idProp)) {
          newMap.set(item[idProp], item);
        }
      }

      // Deletions (present before, missing now)
      for (const [id, prevItem] of prevMap.entries()) {
        if (!newMap.has(id)) {
          diffs.push({ key: `${arrPath}[${idProp}:${id}]`, operation: 'delete', previousValue: prevItem, newValue: undefined });
        }
      }

      // Additions (newly present)
      for (const [id, newItem] of newMap.entries()) {
        if (!prevMap.has(id)) {
          diffs.push({ key: `${arrPath}[${idProp}:${id}]`, operation: 'add', previousValue: undefined, newValue: newItem });
        }
      }

      // Changes (in both; recurse)
      for (const [id, newItem] of newMap.entries()) {
        if (prevMap.has(id)) {
          const prevItem = prevMap.get(id);
          if (typeof newItem === 'object' && newItem !== null && typeof prevItem === 'object' && prevItem !== null) {
            diffs.push(...generateDeepDiffs(prevItem, newItem, `${arrPath}[${idProp}:${id}]`));
          } else if (newItem !== prevItem) {
            diffs.push({ key: `${arrPath}[${idProp}:${id}]`, operation: 'change', previousValue: prevItem, newValue: newItem });
          }
        }
      }

      return;
    }

    // Fallback to index-based diffing if items don't have a clear identity
    const maxLength = Math.max(prevArr.length, newArr.length);
    for (let i = 0; i < maxLength; i++) {
      const prevItem = prevArr[i];
      const newItem = newArr[i];

      if (i >= newArr.length) {
        diffs.push({ key: `${arrPath}[${i}]`, operation: 'delete', previousValue: prevItem, newValue: undefined });
      } else if (i >= prevArr.length) {
        diffs.push({ key: `${arrPath}[${i}]`, operation: 'add', previousValue: undefined, newValue: newItem });
      } else if (typeof newItem === 'object' && newItem !== null && typeof prevItem === 'object' && prevItem !== null) {
        diffs.push(...generateDeepDiffs(prevItem, newItem, `${arrPath}[${i}]`));
      } else if (newItem !== prevItem) {
        diffs.push({ key: `${arrPath}[${i}]`, operation: 'change', previousValue: prevItem, newValue: newItem });
      }
    }
  };

  const allKeys = new Set([...Object.keys(prevObj || {}), ...Object.keys(newObj || {})]);

  for (const key of allKeys) {
    const prevValue = prevObj[key];
    const newValue = newObj[key];
    const currentPath = buildPath(path, key);

    if (Array.isArray(prevValue) && !Array.isArray(newValue)) {
      // Array changed to non-array
      diffs.push({ key: currentPath, operation: 'change', previousValue: prevValue, newValue });
    } else if (!Array.isArray(prevValue) && Array.isArray(newValue)) {
      // Non-array changed to array
      diffs.push({ key: currentPath, operation: 'change', previousValue: prevValue, newValue });
    } else if (Array.isArray(newValue) && Array.isArray(prevValue)) {
      handleArrayDiffs(prevValue, newValue, currentPath);
    } else if (typeof newValue === 'object' && newValue !== null && typeof prevValue === 'object' && prevValue !== null) {
      const isLexical = prevValue?.type === 'lexical' && newValue?.type === 'lexical';
      if (isLexical) {
        const a = serializeLexical(prevValue?.value, {}, { ai: true });
        const b = serializeLexical(newValue?.value, {}, { ai: true });
        if (a?.value !== b?.value) {
          diffs.push({ key: currentPath, operation: 'change', previousValue: prevValue, newValue });
        }
        // We already checked if value is different, how about all other keys in here that are not value or type? Example: timezone
        const otherKeys = Object.keys({ ...prevValue, ...newValue }).filter((k) => k !== 'value' && k !== 'type');
        for (const otherKey of otherKeys) {
          if (prevValue[otherKey] !== newValue[otherKey]) {
            diffs.push({ key: `${currentPath}.${otherKey}`, operation: 'change', previousValue: prevValue[otherKey], newValue: newValue[otherKey] });
          }
        }
        continue;
      }

      const isSelect = prevValue?.type === 'select' && newValue?.type === 'select';
      if (isSelect) {
        if (prevValue?.value !== newValue?.value) {
          diffs.push({ key: currentPath, operation: 'change', previousValue: prevValue, newValue });
        }
        continue;
      }

      if (JSON.stringify(prevValue) !== JSON.stringify(newValue)) {
        diffs.push(...generateDeepDiffs(prevValue, newValue, currentPath));
      }
    } else if (newValue !== prevValue) {
      diffs.push({ key: currentPath, operation: 'change', previousValue: prevValue, newValue });
    }
  }

  return diffs;
};
